import type { ExperienceEntry } from '../../core/types';

/**
 * LinkedIn PDF import.
 *
 * LinkedIn has no public API for reading an arbitrary profile, and scraping one
 * breaks their terms of service. What they *do* offer every user is
 * **More → Save to PDF** on their own profile, which is the one export route
 * that is both sanctioned and available to a student with no API access.
 *
 * That export is a CV-style document, not structured data, so this module is
 * explicitly a best-effort parser. Three consequences shape the design:
 *
 *  1. `parseLinkedInPdfText` is **pure** — text in, fields out. All of the
 *     pdf.js machinery is confined to `extractPdfText`, so the fragile part (the
 *     heuristics) is unit-testable against a captured text fixture and the part
 *     that needs a browser is trivial.
 *  2. Every parse returns `warnings`, and the raw text always comes back. When a
 *     heuristic misses, the user still has their text to paste by hand.
 *  3. The caller is expected to show a preview and ask before overwriting the
 *     form. A silent bad import that wipes someone's typing is worse than no
 *     import at all.
 *
 * The layout below matches LinkedIn's current export. If they change it, this is
 * the only file that needs to change.
 */

/** Section headings LinkedIn emits, lowercased for matching. */
const SECTION_HEADINGS = new Set([
  'contact',
  'top skills',
  'skills',
  'languages',
  'certifications',
  'honors-awards',
  'honors & awards',
  'publications',
  'patents',
  'summary',
  'experience',
  'education',
  'volunteer experience',
  'projects',
  'courses',
  'test scores',
  'organizations',
  'recommendations',
  'interests',
]);

const MONTH = '(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*';

/**
 * A date-range line, which is what anchors experience parsing.
 *
 * LinkedIn always emits one per role and never inside a description, which makes
 * it a far more reliable delimiter than blank lines or indentation — both of
 * which pdf.js flattens away.
 */
const DATE_RANGE = new RegExp(
  `^\\s*(?:${MONTH}\\s+)?(?:19|20)\\d{2}\\s*[-–—]\\s*(?:Present|Current|(?:${MONTH}\\s+)?(?:19|20)\\d{2})`,
  'i'
);

/** A trailing "(1 year 3 months)" duration, which we drop. */
const DURATION_SUFFIX = /\s*\([^)]*\)\s*$/;

/** Lines that are page furniture rather than profile content. */
const NOISE = [
  /^page\s+\d+\s+of\s+\d+$/i,
  /^\(linkedin\)$/i,
  /^\(?www\.linkedin\.com/i,
  /^linkedin\.com/i,
  /^https?:\/\//i,
];

export interface ParsedProfile {
  headline: string;
  about: string;
  experience: ExperienceEntry[];
  skills: string[];
  /** Plain-language notes on anything we could not read. Always show these. */
  warnings: string[];
  /** The full extracted text, so a failed parse still leaves something usable. */
  rawText: string;
}

function isNoise(line: string): boolean {
  return NOISE.some((pattern) => pattern.test(line));
}

interface Section {
  heading: string;
  lines: string[];
}

/** Splits the document into `{ heading, lines }` blocks, in document order. */
function splitIntoSections(lines: string[]): Section[] {
  const sections: Section[] = [{ heading: '', lines: [] }];

  for (const line of lines) {
    const key = line.toLowerCase().trim();
    if (SECTION_HEADINGS.has(key)) {
      sections.push({ heading: key, lines: [] });
    } else {
      sections[sections.length - 1]?.lines.push(line);
    }
  }

  return sections;
}

/**
 * Lifts the name/headline/location preamble out of the block it landed in.
 *
 * LinkedIn emits the whole left sidebar (Contact, Top Skills, Languages,
 * Certifications) before the main column, so the three unlabelled lines that open
 * the main column — name, headline, and usually a location — come out *inside*
 * the last sidebar block. Which block that is depends on what the visitor has
 * filled in: for most people it is Languages or Certifications, and nothing
 * notices. For someone with neither it is Top Skills, and their own name and city
 * get imported as skills, inflating the skills count and the keyword coverage.
 *
 * Lifting the lines out once, here, is what stops that: the skills list and the
 * headline then read from sets that cannot overlap.
 */
function liftPreamble(sections: Section[]): { sections: Section[]; preamble: string[] } {
  const mainIndex = sections.findIndex(
    (section) => section.heading === 'summary' || section.heading === 'experience'
  );
  const host = mainIndex > 0 ? sections[mainIndex - 1] : undefined;
  if (!host) return { sections, preamble: [] };

  // Name, headline, and a location line that is only there if the visitor set
  // one. Assuming all three unconditionally would eat the last real skill off a
  // profile with no location, and then read the name as the headline.
  const tail = host.lines.slice(-3);
  const size = tail.length === 3 && !looksLikeLocation(tail[2] ?? '') ? 2 : tail.length;
  const preamble = host.lines.slice(host.lines.length - size);
  if (preamble.length === 0) return { sections, preamble };

  return {
    sections: sections.map((section) =>
      section === host ? { ...section, lines: section.lines.slice(0, -size) } : section
    ),
    preamble,
  };
}

/**
 * Reads experience entries out of the Experience block.
 *
 * Anchored on date-range lines: for each one, the line above is the job title and
 * the line above that is the organisation, which is the order LinkedIn emits.
 * Everything after the date line — minus a bare location line, if present — is
 * the description, up to the next entry's organisation.
 */
function parseExperience(lines: string[]): { entries: ExperienceEntry[]; warnings: string[] } {
  const warnings: string[] = [];
  const dateIndices = lines.reduce<number[]>((found, line, index) => {
    if (DATE_RANGE.test(line)) found.push(index);
    return found;
  }, []);

  if (dateIndices.length === 0) {
    if (lines.length > 0) {
      warnings.push(
        'Found an Experience section but no recognisable date ranges, so the roles could not be split up. Paste them in by hand.'
      );
    }
    return { entries: [], warnings };
  }

  const entries: ExperienceEntry[] = [];

  dateIndices.forEach((dateIndex, position) => {
    const title = lines[dateIndex - 1] ?? '';
    const organisation = lines[dateIndex - 2] ?? '';

    // The description runs to just before the next entry's organisation line.
    const nextDateIndex = dateIndices[position + 1];
    const end =
      nextDateIndex === undefined ? lines.length : Math.max(dateIndex + 1, nextDateIndex - 2);

    let body = lines.slice(dateIndex + 1, end);

    // LinkedIn sometimes emits a second date line (multiple stints at one
    // employer) and often a bare location line. Both are metadata, not content.
    body = body.filter((line, index) => !(index === 0 && looksLikeLocation(line)));
    body = body.filter((line) => !DATE_RANGE.test(line));

    // Skip an entry we could not name at all — an unnamed, empty entry would
    // trigger rules about incomplete entries that the user never created.
    if (!title.trim() && !organisation.trim() && body.length === 0) return;

    entries.push({
      id: `pdf-${position + 1}`,
      title: title.replace(DURATION_SUFFIX, '').trim(),
      organisation: organisation.trim(),
      description: body.join('\n').trim(),
    });
  });

  if (entries.length > 0) {
    warnings.push(
      `Split the Experience section into ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}. Check the titles and organisations landed in the right fields — the PDF does not label them.`
    );
  }

  return { entries, warnings };
}

/** "Bengaluru, Karnataka, India" — short, comma-separated, no verb. */
function looksLikeLocation(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed.length > 60) return false;
  if (!trimmed.includes(',')) return false;
  // A description bullet nearly always runs longer than four words per clause.
  return trimmed.split(/\s+/).length <= 8;
}

export function parseLinkedInPdfText(rawText: string): ParsedProfile {
  const lines = rawText
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter((line) => line.length > 0 && !isNoise(line));

  const { sections, preamble } = liftPreamble(splitIntoSections(lines));
  const warnings: string[] = [];

  const find = (heading: string) => sections.find((section) => section.heading === heading);

  /* ------------------------------------------------------------- skills --- */
  const skillLines = [...(find('top skills')?.lines ?? []), ...(find('skills')?.lines ?? [])];
  const skills = [...new Set(skillLines.filter((line) => line.length <= 60))];
  if (skills.length === 0) {
    warnings.push('No Top Skills section found in the PDF, so skills are empty. Add them by hand.');
  }

  /* -------------------------------------------------------------- about --- */
  const about = (find('summary')?.lines ?? []).join('\n\n').trim();
  if (!about) {
    warnings.push('No Summary section in the PDF — that usually means the About section is empty.');
  }

  /* ----------------------------------------------------------- headline --- */
  // The name and headline sit in the unlabelled block immediately before Summary
  // or Experience, after the sidebar sections — `liftPreamble` has already pulled
  // those lines aside. Line 1 is the name, line 2 the headline. We deliberately do
  // not import the name: nothing in the engine scores it, so there is no reason to
  // hold it.
  const headline = (preamble.length >= 2 ? preamble[1] : '') ?? '';
  if (!headline) {
    warnings.push(
      'Could not identify the headline. It is the line directly under your name — paste it in.'
    );
  }

  /* --------------------------------------------------------- experience --- */
  const experienceSection = find('experience');
  const { entries, warnings: experienceWarnings } = parseExperience(experienceSection?.lines ?? []);
  warnings.push(...experienceWarnings);
  if (!experienceSection) {
    warnings.push('No Experience section found in the PDF.');
  }

  return { headline, about, experience: entries, skills, warnings, rawText };
}

/** Rejects an implausibly large file before it reaches the PDF worker. */
export const MAX_PDF_BYTES = 10 * 1024 * 1024;

/**
 * Pulls the text out of a PDF with pdf.js, entirely in the browser.
 *
 * pdf.js is imported dynamically so its ~400 kB stays out of the initial bundle
 * — most visitors paste their text and never touch this path. The worker URL is
 * resolved by Vite through the `?url` import rather than hardcoded, so it keeps
 * working under a GitHub Pages base path and across pdf.js upgrades.
 */
export async function extractPdfText(file: File): Promise<string> {
  if (file.size > MAX_PDF_BYTES) {
    throw new Error(
      `That file is larger than ${MAX_PDF_BYTES / 1024 / 1024} MB. Is it definitely the LinkedIn PDF export?`
    );
  }

  const [pdfjs, workerUrl] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url').then((module) => module.default),
  ]);

  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const data = new Uint8Array(await file.arrayBuffer());
  // `destroy()` lives on the loading task rather than the document proxy, so the
  // task has to be held onto — otherwise the worker and its copy of the file
  // stay alive for the life of the page, which is a real leak if someone imports
  // several PDFs in a row.
  const loading = pdfjs.getDocument({ data });

  try {
    const doc = await loading.promise;
    const pages: string[] = [];
    for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber++) {
      const page = await doc.getPage(pageNumber);
      const content = await page.getTextContent();
      pages.push(itemsToLines(content.items));
      page.cleanup();
    }
    return pages.join('\n');
  } finally {
    await loading.destroy();
  }
}

interface TextItemLike {
  str?: string;
  hasEOL?: boolean;
}

/**
 * Rebuilds lines from pdf.js text items.
 *
 * pdf.js yields positioned runs, not lines, and sets `hasEOL` where the original
 * layout broke. Honouring that flag is what preserves one-bullet-per-line, which
 * the experience rules depend on to count bullets at all.
 */
function itemsToLines(items: readonly unknown[]): string {
  let out = '';
  for (const item of items) {
    const text = item as TextItemLike;
    if (typeof text.str !== 'string') continue;
    out += text.str;
    if (text.hasEOL) out += '\n';
  }
  return out;
}

export async function importLinkedInPdf(file: File): Promise<ParsedProfile> {
  const text = await extractPdfText(file);
  if (!text.trim()) {
    return {
      headline: '',
      about: '',
      experience: [],
      skills: [],
      warnings: [
        'No text could be read from that PDF. If it is a scan or a screenshot there is no text layer to extract — use More → Save to PDF on your own LinkedIn profile to get one.',
      ],
      rawText: '',
    };
  }
  return parseLinkedInPdfText(text);
}
